import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useToast } from '@/hooks/use-toast'
import { Plus, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const TYPE_META: Record<string, string> = {
  feature:     'bg-green-100 text-green-700',
  fix:         'bg-red-100 text-red-700',
  improvement: 'bg-blue-100 text-blue-700',
  breaking:    'bg-amber-100 text-amber-700',
}

interface Props {
  platformRole: string
}

export function ChangelogView({ platformRole }: Props) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const canManage = platformRole === 'super_admin'
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState({ version: '', title: '', description: '', type: 'feature' })
  const [submitting, setSubmitting] = useState(false)

  const { data: entries } = useQuery({
    queryKey: ['platform-changelog'],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_changelog')
        .select('*')
        .order('created_at', { ascending: false })
      return data || []
    }
  })

  async function togglePublished(id: string, published: boolean) {
    await supabase.from('platform_changelog').update({ published }).eq('id', id)
    qc.invalidateQueries({ queryKey: ['platform-changelog'] })
  }

  async function addRelease() {
    if (!form.version.trim() || !form.title.trim() || !form.description.trim()) return
    setSubmitting(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('platform_changelog').insert({
        version: form.version.trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        published: false,
        created_by: user?.id,
      })
      toast({ title: 'Release added.' })
      setForm({ version: '', title: '', description: '', type: 'feature' })
      setAddOpen(false)
      qc.invalidateQueries({ queryKey: ['platform-changelog'] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Changelog</h1>
          <p className="text-justify text-muted-foreground text-sm mt-1">Platform release history.</p>
        </div>
        {canManage && (
          <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setAddOpen(true)}>
            <Plus className="w-3.5 h-3.5" />
            Add Release
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {(entries ?? []).length === 0 ? (
          <p className="text-justify text-sm text-muted-foreground text-center py-12">No releases recorded yet.</p>
        ) : (entries ?? []).map(entry => (
          <div key={entry.id} className="rounded-lg border p-4 bg-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="font-mono text-xs">{entry.version}</Badge>
                <Badge className={cn('text-[10px] capitalize', TYPE_META[entry.type] ?? TYPE_META.feature)}>
                  {entry.type}
                </Badge>
                {!entry.published && <Badge variant="secondary" className="text-[10px]">Draft</Badge>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground">
                  {new Date(entry.created_at).toLocaleDateString()}
                </span>
                {canManage && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Published</span>
                    <Switch checked={entry.published} onCheckedChange={v => togglePublished(entry.id, v)} />
                  </div>
                )}
              </div>
            </div>
            <h3 className="text-sm font-semibold mt-2">{entry.title}</h3>
            <p className="text-sm text-muted-foreground text-justify leading-relaxed mt-1">{entry.description}</p>
          </div>
        ))}
      </div>

      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent side="right" className="w-[460px]">
          <SheetHeader>
            <SheetTitle>Add Release</SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label>Version</Label>
              <Input
                value={form.version}
                onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
                placeholder="e.g. v1.2.0"
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature">Feature</SelectItem>
                  <SelectItem value="fix">Fix</SelectItem>
                  <SelectItem value="improvement">Improvement</SelectItem>
                  <SelectItem value="breaking">Breaking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Short summary"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What changed?"
                className="resize-none"
              />
            </div>
            <Button
              className="w-full"
              disabled={submitting || !form.version.trim() || !form.title.trim() || !form.description.trim()}
              onClick={addRelease}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Release
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
